import { ReactNode } from 'react';
import { Box, ColumnLayout, Container, Header, SpaceBetween } from '@amzn/awsui-components-react';
import { SkeletonBar } from '../skeletonBar';
import './activitySummary.scss';

const MAX_COLUMNS = 4;

export interface ActivitySummaryPanelProps {
  header: string;
  leadSentence?: string;
  // Only the tiles that should show. Hidden and failed tiles are left out by the caller. Lays out
  // at most 4 columns, so a fifth tile wraps onto a second row.
  tiles: ReactNode[];
}

/**
 * Shared shell for the Volunteer and Coordinator summaries (cce-4530, cce-8083): a header, an
 * optional lead sentence, and a row of tiles with column dividers. Renders nothing when no tile is
 * left, which covers the "every tile failed" state.
 */
export function ActivitySummaryPanel({ header, leadSentence, tiles }: ActivitySummaryPanelProps) {
  if (tiles.length === 0) return null;

  return (
    <Container header={<Header variant="h2">{header}</Header>}>
      <SpaceBetween size="m">
        {leadSentence && <Box fontSize="heading-l">{leadSentence}</Box>}
        <div className="activity-summary-tiles">
          <ColumnLayout columns={Math.min(tiles.length, MAX_COLUMNS)} variant="text-grid">
            {tiles}
          </ColumnLayout>
        </div>
      </SpaceBetween>
    </Container>
  );
}

export interface ActivitySummaryPanelSkeletonProps {
  header: string;
  // Announced to screen readers in place of the decorative bars.
  loadingText: string;
}

/**
 * Stands in for the panel while its numbers load, sized like a full row of tiles so the tabs below
 * stay put when the numbers arrive.
 */
export function ActivitySummaryPanelSkeleton({
  header,
  loadingText,
}: ActivitySummaryPanelSkeletonProps) {
  return (
    <Container header={<Header variant="h2">{header}</Header>}>
      <div role="status" aria-live="polite">
        <span className="visually-hidden">{loadingText}</span>
        <SpaceBetween size="m">
          <SkeletonBar width="60%" height="24px" />
          <div className="activity-summary-tiles">
            <ColumnLayout columns={MAX_COLUMNS} variant="text-grid">
              {Array.from({ length: MAX_COLUMNS }, (_, index) => (
                <SpaceBetween key={index} size="s">
                  <SkeletonBar width="70%" height="16px" />
                  <SkeletonBar width="40%" height="44px" />
                  <SkeletonBar width="50%" height="14px" />
                </SpaceBetween>
              ))}
            </ColumnLayout>
          </div>
        </SpaceBetween>
      </div>
    </Container>
  );
}
